/**
 * Conversation thread – display messages and send new ones (offer or project context).
 */

import { useState, useEffect, useRef } from 'react';
import {
	getOrCreateConversationByOffer,
	getProjectConversation,
	sendConversationMessage,
	type Conversation,
	type ConversationMessage
} from '@services/conversations';
import { useI18n } from '../i18n';

type Variant = 'offer' | 'project';

export interface ConversationThreadProps {
	variant: Variant;
	offerId?: number;
	projectId?: number;
	currentUserId: number;
	otherPartyName?: string;
	title?: string;
	onClose?: () => void;
	className?: string;
}

export default function ConversationThread({
	variant,
	offerId,
	projectId,
	currentUserId,
	otherPartyName,
	title,
	onClose,
	className = ''
}: ConversationThreadProps) {
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';
	const [conversation, setConversation] = useState<Conversation | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [newBody, setNewBody] = useState('');
	const [sending, setSending] = useState(false);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const getRoleLabel = (role?: string | null): string => {
		switch ((role || '').toLowerCase()) {
			case 'company':
				return 'Company';
			case 'gardener':
				return 'Gardener';
			case 'seeker':
				return 'Seeker';
			case 'admin':
				return 'Admin';
			default:
				return 'User';
		}
	};

	const loadConversation = async () => {
		if (!currentUserId) return;
		setLoading(true);
		setError(null);
		try {
			if (variant === 'offer' && offerId) {
				const data = await getOrCreateConversationByOffer(offerId, currentUserId);
				setConversation(data);
			} else if (variant === 'project' && projectId) {
				const data = await getProjectConversation(projectId, currentUserId);
				setConversation(data);
			} else {
				setError('Missing offer or project context');
			}
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Failed to load conversation');
			setConversation(null);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadConversation();
	}, [variant, offerId, projectId, currentUserId]);

	// Only scroll to bottom when user sends a new message (in handleSend), not on initial load

	const handleSend = async () => {
		const body = newBody.trim();
		if (!body || !conversation || sending) return;
		setSending(true);
		try {
			const msg = await sendConversationMessage(conversation.id, currentUserId, body);
			setConversation((prev) =>
				prev
					? { ...prev, messages: [...(prev.messages || []), msg], updated_at: msg.created_at }
					: prev
			);
			setNewBody('');
			setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
		} catch (e) {
			// Keep draft
			setError(e instanceof Error ? e.message : 'Failed to send message');
		} finally {
			setSending(false);
		}
	};

	const displayTitle = title ?? (otherPartyName ? copy(`Conversation with ${otherPartyName}`, `Unterhaltung mit ${otherPartyName}`, `Razgovor s ${otherPartyName}`) : copy('Messages', 'Nachrichten', 'Poruke'));

	if (loading) {
		return (
			<div className={`conversation-thread ${className}`}>
				{(title || onClose) && (
					<div className="d-flex align-items-center justify-content-between mb-3">
						{title && <h6 className="mb-0">{displayTitle}</h6>}
						{onClose && (
							<button type="button" className="btn btn-sm btn-link text-muted" onClick={onClose} aria-label="Close">
								<i className="bi bi-x-lg" />
							</button>
						)}
					</div>
				)}
				<div className="text-center py-4">
					<div className="spinner-border spinner-border-sm text-success" role="status">
						<span className="visually-hidden">Loading...</span>
					</div>
					<p className="small text-muted mt-2 mb-0">Loading messages...</p>
				</div>
			</div>
		);
	}

	if (error && !conversation) {
		return (
			<div className={`conversation-thread ${className}`}>
				{(title || onClose) && (
					<div className="d-flex align-items-center justify-content-between mb-3">
						{title && <h6 className="mb-0">{displayTitle}</h6>}
						{onClose && (
							<button type="button" className="btn btn-sm btn-link text-muted" onClick={onClose} aria-label="Close">
								<i className="bi bi-x-lg" />
							</button>
						)}
					</div>
				)}
				<div className="alert alert-warning mb-0">{error}</div>
			</div>
		);
	}

	const messages: ConversationMessage[] = conversation?.messages ?? [];

	return (
		<div className={`conversation-thread d-flex flex-column ${className}`}>
			{(displayTitle || onClose) && (
				<div className="d-flex align-items-center justify-content-between mb-2">
					<h6 className="mb-0 text-success">{displayTitle}</h6>
					{onClose && (
						<button type="button" className="btn btn-sm btn-link text-muted p-0" onClick={onClose} aria-label="Close">
							<i className="bi bi-x-lg" />
						</button>
					)}
				</div>
			)}
			<div
				ref={scrollContainerRef}
				className="flex-grow-1 overflow-auto border rounded bg-light bg-opacity-50 p-2 mb-2"
				style={{ minHeight: 200, maxHeight: 360 }}
			>
				{messages.length === 0 ? (
					<div className="text-center text-muted small py-4">
						{copy('No messages yet. Send one to start the conversation.', 'Noch keine Nachrichten. Senden Sie eine Nachricht, um die Unterhaltung zu starten.', 'Još nema poruka. Pošaljite jednu za početak razgovora.')}
					</div>
				) : (
					messages.map((msg) => {
						const isOwn = msg.sender_user_id === currentUserId;
						const senderLabel = `${getRoleLabel(msg.sender_role)}: ${msg.sender_name}`;
						return (
							<div
								key={msg.id}
								className={`d-flex mb-2 ${isOwn ? 'justify-content-end' : 'justify-content-start'}`}
							>
								<div
									className={`rounded px-3 py-2 shadow-sm ${
										isOwn ? 'bg-success text-white' : 'bg-white border'
									}`}
									style={{ maxWidth: '85%' }}
								>
									<div className={`small fw-medium mb-1 ${isOwn ? 'text-white text-opacity-75' : 'text-muted'}`}>
										{senderLabel}
									</div>
									<div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.body}</div>
									<div className={`small mt-1 ${isOwn ? 'text-white text-opacity-75' : 'text-muted'}`}>
										{new Date(msg.created_at).toLocaleString(locale, {
											day: 'numeric',
											month: 'short',
											hour: '2-digit',
											minute: '2-digit'
										})}
									</div>
								</div>
							</div>
						);
					})
				)}
				<div ref={messagesEndRef} />
			</div>
			<div className="d-flex gap-2">
				<textarea
					className="form-control form-control-sm"
					rows={2}
					placeholder={copy('Type a message...', 'Nachricht eingeben...', 'Upišite poruku...')}
					value={newBody}
					onChange={(e) => setNewBody(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && !e.shiftKey) {
							e.preventDefault();
							handleSend();
						}
					}}
					disabled={sending}
					aria-label={copy('Message text', 'Nachrichtentext', 'Tekst poruke')}
				/>
				<button
					type="button"
					className="btn btn-success btn-sm align-self-end"
					onClick={handleSend}
					disabled={sending || !newBody.trim()}
					aria-label={copy('Send message', 'Nachricht senden', 'Pošalji poruku')}
				>
					{sending ? (
						<span className="spinner-border spinner-border-sm" role="status" aria-hidden />
					) : (
						<i className="bi bi-send" />
					)}
				</button>
			</div>
		</div>
	);
}
